import { useEffect, useState } from "react";
import { Skeleton } from "@/components/shadcn/skeleton";
import type { PendingKind } from "./chatStream";

const BRAILLE_FRAMES = ["⠋", "⠙", "⠹", "⠸", "⠼", "⠴", "⠦", "⠧", "⠇", "⠏"];
const BRAILLE_FRAME_MS = 80;

export const BrailleSpinner = () => {
  const [frame, setFrame] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(
      () => setFrame((current) => (current + 1) % BRAILLE_FRAMES.length),
      BRAILLE_FRAME_MS
    );
    return () => window.clearInterval(timer);
  }, []);

  return (
    <span className="inline-block w-3 text-center text-sm leading-none text-slate-500" aria-hidden="true">
      {BRAILLE_FRAMES[frame]}
    </span>
  );
};

const PENDING_LABELS: Record<PendingKind, string> = {
  table: "Building table…",
  chart: "Drawing chart…",
  list: "Preparing list…",
};

const SKELETON_CLASS = "motion-safe:animate-pulse motion-reduce:animate-none";

const TableSkeleton = () => (
  <div className="space-y-2">
    <Skeleton className={`h-4 w-1/3 ${SKELETON_CLASS}`} />
    <div className="grid grid-cols-3 gap-2">
      {Array.from({ length: 12 }, (_, index) => (
        <Skeleton key={index} className={`h-3 ${index < 3 ? "bg-slate-300" : ""} ${SKELETON_CLASS}`} />
      ))}
    </div>
  </div>
);

const CHART_BAR_HEIGHTS = ["40%", "65%", "50%", "85%", "60%", "75%"];

const ChartSkeleton = () => (
  <div className="space-y-2">
    <Skeleton className={`h-4 w-1/3 ${SKELETON_CLASS}`} />
    <div className="flex h-64 w-full items-end gap-3 rounded-md border border-slate-200 px-4 pb-4">
      {CHART_BAR_HEIGHTS.map((height, index) => (
        <Skeleton key={index} className={`flex-1 ${SKELETON_CLASS}`} style={{ height }} />
      ))}
    </div>
  </div>
);

const ListSkeleton = () => (
  <div className="space-y-2">
    <Skeleton className={`h-4 w-1/3 ${SKELETON_CLASS}`} />
    {[70, 55, 65].map((width, index) => (
      <div key={index} className="flex items-center gap-2 pl-2">
        <Skeleton className={`size-1.5 rounded-full ${SKELETON_CLASS}`} />
        <Skeleton className={`h-3 ${SKELETON_CLASS}`} style={{ width: `${width}%` }} />
      </div>
    ))}
  </div>
);

const SKELETONS: Record<PendingKind, () => React.JSX.Element> = {
  table: TableSkeleton,
  chart: ChartSkeleton,
  list: ListSkeleton,
};

export const ReplyProgress = ({
  pendingKind,
  statusLabel,
}: {
  pendingKind: PendingKind | null;
  statusLabel: string | null;
}) => {
  const label = pendingKind ? PENDING_LABELS[pendingKind] : (statusLabel ?? "Still working…");
  const Placeholder = pendingKind ? SKELETONS[pendingKind] : null;

  return (
    <div className="space-y-2" data-testid="reply-progress">
      {Placeholder ? (
        <div aria-hidden="true">
          <Placeholder />
        </div>
      ) : null}
      <span className="inline-flex items-center gap-2">
        <BrailleSpinner />
        <span className="text-xs italic text-slate-500">{label}</span>
      </span>
    </div>
  );
};
